/**
 * Builds a {@link QueryPlan} from a normalized input.
 *
 * This is the bridge from the public contract to the internal model that all
 * compilers consume. It also generates deterministic output names for
 * aggregations and date groups (once), so every engine stays consistent.
 */
import type {
    AggregationDefinition,
    FilterExpression,
    HavingExpression,
    OrderByDefinition,
} from '../input/input-types';
import type { NormalizedInput } from '../input/normalize-input';
import { OutputNameGenerator } from '../metadata/output-name-generator';
import {
    AggregationNode,
    FieldRefNode,
    FilterConditionNode,
    FilterNode,
    GroupFieldNode,
    GroupingNode,
    HavingConditionNode,
    HavingNode,
    LimitNode,
    LogicalFilterNode,
    LogicalHavingNode,
    NotFilterNode,
    NotHavingNode,
    OrderNode,
    PaginationNode,
    ProjectionNode,
    QueryPlan,
    SourceNode,
} from './query-plan';

export function inputToPlan(input: NormalizedInput): QueryPlan {
    const names = new OutputNameGenerator();

    const source = new SourceNode(
        '$.source.name',
        input.source.name,
        input.source.schema ?? null,
    );

    // Reserve selected field names so generated names don't collide with them.
    // Group output names are generated (and reserved) below via the name
    // generator, so we must NOT pre-reserve the raw group field names here.
    input.select?.forEach((s) => names.reserve(s.field));

    const projection = input.select?.length
        ? new ProjectionNode(
              '$.select',
              input.select.map(
                  (s, i) =>
                      new FieldRefNode(
                          `$.select[${i}]`,
                          s.field,
                          s.type,
                          s.path ?? null,
                      ),
              ),
          )
        : null;

    const filter = input.filter ? buildFilter(input.filter, '$.filter') : null;

    const aggregations = (input.aggregations ?? []).map((a, i) =>
        buildAggregation(a, `$.aggregations[${i}]`, names),
    );

    const grouping = input.groupBy?.length
        ? new GroupingNode(
              '$.groupBy',
              input.groupBy.map(
                  (g, i) =>
                      new GroupFieldNode(
                          `$.groupBy[${i}]`,
                          new FieldRefNode(
                              `$.groupBy[${i}]`,
                              g.field,
                              g.type,
                              g.path ?? null,
                          ),
                          g.granularity ?? null,
                          g.offset ?? null,
                          g.limit ?? null,
                          names.dateGroupName(g.field, g.granularity ?? null),
                      ),
              ),
          )
        : null;

    const having = input.having
        ? buildHaving(input.having, '$.having', aggregations, names)
        : null;

    const ordering = (input.orderBy ?? []).map((o, i) =>
        buildOrder(o, `$.orderBy[${i}]`, aggregations, names),
    );

    const pagination = new PaginationNode('$', input.skip, input.cursor);
    const limits = new LimitNode('$.limit', input.limit);

    return new QueryPlan(
        '$',
        source,
        projection,
        filter,
        grouping,
        aggregations,
        having,
        ordering,
        pagination,
        limits,
    );
}

function buildAggregation(
    def: AggregationDefinition,
    path: string,
    names: OutputNameGenerator,
): AggregationNode {
    // TODO: plan validation should require `type` when `field` is present.
    // Kept lenient here (aggregations produce numbers).
    const field = def.field
        ? new FieldRefNode(
              path,
              def.field,
              def.type ?? 'number',
              def.path ?? null,
          )
        : null;
    return new AggregationNode(
        path,
        def.function,
        field,
        names.aggregationName(def.function, def.field ?? null),
    );
}

function buildFilter(expr: FilterExpression, path: string): FilterNode {
    return buildFilterNode(expr, path);
}

/** Build a {@link FilterNode} tree from a filter expression (shared with mutations). */
export function buildFilterNode(
    expr: FilterExpression,
    path: string,
): FilterNode {
    if ('and' in expr) {
        return new LogicalFilterNode(
            path,
            'and',
            expr.and.map((e, i) => buildFilter(e, `${path}.and[${i}]`)),
        );
    }
    if ('or' in expr) {
        return new LogicalFilterNode(
            path,
            'or',
            expr.or.map((e, i) => buildFilter(e, `${path}.or[${i}]`)),
        );
    }
    if ('not' in expr) {
        return new NotFilterNode(path, buildFilter(expr.not, `${path}.not`));
    }
    return new FilterConditionNode(
        path,
        new FieldRefNode(path, expr.field, expr.type, expr.path ?? null),
        expr.operator,
        expr.value,
    );
}

function buildHaving(
    expr: HavingExpression,
    path: string,
    aggregations: AggregationNode[],
    names: OutputNameGenerator,
): HavingNode {
    if ('and' in expr) {
        return new LogicalHavingNode(
            path,
            'and',
            expr.and.map((e, i) =>
                buildHaving(e, `${path}.and[${i}]`, aggregations, names),
            ),
        );
    }
    if ('or' in expr) {
        return new LogicalHavingNode(
            path,
            'or',
            expr.or.map((e, i) =>
                buildHaving(e, `${path}.or[${i}]`, aggregations, names),
            ),
        );
    }
    if ('not' in expr) {
        return new NotHavingNode(
            path,
            buildHaving(expr.not, `${path}.not`, aggregations, names),
        );
    }
    return new HavingConditionNode(
        path,
        resolveAggregation(expr.aggregation, path, aggregations, names),
        expr.operator,
        expr.value,
    );
}

function buildOrder(
    def: OrderByDefinition,
    path: string,
    aggregations: AggregationNode[],
    names: OutputNameGenerator,
): OrderNode {
    if ('aggregation' in def) {
        return new OrderNode(
            path,
            {
                kind: 'aggregation',
                aggregation: resolveAggregation(
                    def.aggregation,
                    path,
                    aggregations,
                    names,
                ),
            },
            def.direction,
        );
    }
    return new OrderNode(
        path,
        {
            kind: 'field',
            field: new FieldRefNode(
                path,
                def.field,
                def.type,
                def.path ?? null,
            ),
        },
        def.direction,
    );
}

/** Reuse the matching aggregation node when possible; else create a new one. */
function resolveAggregation(
    def: AggregationDefinition,
    path: string,
    aggregations: AggregationNode[],
    names: OutputNameGenerator,
): AggregationNode {
    const match = aggregations.find(
        (a) =>
            a.fn === def.function &&
            (a.field?.field ?? null) === (def.field ?? null),
    );
    return match ?? buildAggregation(def, path, names);
}
